<template>
  <div class="not-found-pages">
    <HeaderAdmin />

    <div class="dashboard-shell workspace-stack">
      <section class="glass-surface workspace-hero workspace-hero--event">
        <div class="workspace-hero__content workspace-hero__content--event">
          <div class="workspace-hero__context">
            <router-link class="workspace-button workspace-button--small workspace-button--ghost" :to="{ name: 'events' }">
              <i class="fas fa-arrow-left"></i>
              <span>Volver a eventos</span>
            </router-link>

            <div class="workspace-badge-row workspace-badge-row--compact workspace-context-pills">
              <span class="workspace-status-pill" :class="event.review_status === 'approved' ? 'is-active' : 'is-warning'">{{ eventStatusLabel }}</span>
              <span class="workspace-badge">
                <i class="fas fa-pen-ruler"></i>
                <span>{{ editModeLabel }}</span>
              </span>
            </div>
          </div>

          <span class="workspace-kicker">Evento</span>
          <div class="workspace-badge-row workspace-badge-row--compact workspace-hero__badges">
            <span class="workspace-badge">{{ eventStatusLabel }}</span>
            <span class="workspace-badge">{{ formatDateLabel(event.start_date) }}</span>
            <span class="workspace-badge">{{ eventVenueName }}</span>
          </div>
          <h1 class="dashboard-title">{{ event.name || 'Tu evento en control total' }}</h1>
          <p class="dashboard-subtitle">Usa las secciones de abajo para editar el evento, mover la operación y revisar finanzas sin duplicar menú.</p>

          <div class="workspace-actions workspace-actions--compact">
            <button class="workspace-button" type="button" @click="openPublicEvent" :disabled="!event.slug || event.review_status !== 'approved' || Number(event.type) !== 1">
              <i class="fas fa-external-link-alt"></i>
              <span>{{ Number(event.type) === 1 ? 'Ver checkout' : 'Aún no lanzado' }}</span>
            </button>
          </div>

          <div class="workspace-hero__notice">
            <i class="fas fa-circle-check"></i>
            <div>
              <strong>{{ editModeLabel }}</strong>
              <small>Las pestañas de abajo concentran la edición, etapas, códigos, promos, asistencia y finanzas en una sola navegación.</small>
            </div>
          </div>

          <div class="workspace-hero__summary">
            <article>
              <small>Estado</small>
              <strong>{{ heroHeadline }}</strong>
            </article>
            <article>
              <small>Panorama</small>
              <span>{{ heroSubline }}</span>
            </article>
          </div>
        </div>

        <article class="workspace-event-spotlight" :class="{ 'is-placeholder': !event.banner_url }">
          <img v-if="event.banner_url" :src="event.banner_url" :alt="event.name" class="workspace-event-spotlight__media" />
          <div v-else class="workspace-event-spotlight__placeholder">
              <span>Vista previa</span>
            <strong>{{ event.name || 'Fomo Event' }}</strong>
          </div>

          <div class="workspace-event-spotlight__overlay"></div>

          <div class="workspace-event-spotlight__copy">
            <div class="workspace-event-spotlight__metrics">
              <span><i class="fas fa-calendar-alt"></i>{{ formatDateLabel(event.start_date) }}</span>
              <span><i class="fas fa-map-marker-alt"></i>{{ eventVenueName }}</span>
            </div>

            <div class="workspace-event-spotlight__footer">
              <small>{{ spotlightMetricLabel }}</small>
              <strong>{{ spotlightMetricValue }}</strong>
            </div>
          </div>
        </article>
      </section>

      <section class="workspace-metrics">
        <article class="workspace-metric">
          <small>Facturado</small>
          <strong>{{ formatMoney(event.total_ticket_sales) }}</strong>
          <p>Solo ticketing aprobado del evento</p>
        </article>
        <article class="workspace-metric">
          <small>Vendidos</small>
          <strong>{{ Number(event.total_ticket_count || 0) }}</strong>
          <p>Tickets aprobados en total</p>
        </article>
        <article class="workspace-metric">
          <small>Cortesias</small>
          <strong>{{ Number(event.total_is_courtesy_count || 0) }}</strong>
          <p>Accesos de cortesia emitidos</p>
        </article>
        <article v-if="canViewInternalFinance" class="workspace-metric">
          <small>Fomo Safe</small>
          <strong>{{ event.fomo_safe_enabled ? 'On' : 'Off' }}</strong>
          <p>{{ event.fomo_safe_enabled ? 'Proteccion activa para este evento' : 'Puedes activarla cuando la necesites' }}</p>
        </article>
        <article v-if="canViewInternalFinance" class="workspace-metric">
          <small>Addons netos</small>
          <strong>{{ formatMoney(event.total_addons_net) }}</strong>
          <p>{{ event.total_addons_units || 0 }} unidades vendidas</p>
        </article>
        <article v-if="!canViewInternalFinance" class="workspace-metric">
          <small>Ganancia organizer</small>
          <strong>{{ formatMoney(event.ledger_organizer_sales_gross) }}</strong>
          <p>Lo acumulado para tu evento.</p>
        </article>
        <article v-if="!canViewInternalFinance" class="workspace-metric">
          <small>Pendiente por depositar</small>
          <strong>{{ formatMoney(event.ledger_organizer_pending) }}</strong>
          <p>{{ formatMoney(event.ledger_organizer_deposited) }} ya depositado.</p>
        </article>
      </section>

      <article v-if="!loadError" class="glass-surface workspace-panel workspace-panel--sticky-top">
        <div class="workspace-toolbar workspace-toolbar--tabs">
          <button v-for="tab in tabs" :key="tab.name" :class="['workspace-tab', { 'is-active': value === tab.name }]" type="button" @click="value = tab.name">
            <i :class="tab.icon"></i>
            <span>{{ tab.name }}</span>
          </button>
        </div>
      </article>

      <TestView
        v-show="value === 'Resumen'"
        v-if="!showLoading && !loadError"
        :card="event.total_cards"
        :cardPos="event.total_cardsPos"
        :bank="event.total_bank_transfers"
        :cash="event.total_cash"
        :generatedData="generatedData"
        :generatedData2="generatedData2"
        :total_tickets="total_tickets"
        :tickets="event.total_ticket_count"
        :sales="event.total_ticket_sales"
        :courtesies="event.total_is_courtesy_count"
        :eventTickets="eventTickets"
        :event="event"
        style="padding-top: 20px; padding-bottom: 20px;"
      />

      <EventEditPanel v-show="value === 'Editar'" v-if="!showLoading && !loadError" :event="event" :event-id="eventId" @event-updated="applyEventUpdate" />
      <StagesView v-show="value === 'Etapas'" v-if="!showLoading && !loadError" :event="event" style="padding-top: 20px; padding-bottom: 20px;" />
      <CodesView v-show="value === 'Codigos'" v-if="!showLoading && !loadError" :event="event" style="padding-top: 20px; padding-bottom: 20px;" />
      <PromoTable v-show="value === 'Promociones'" v-if="!showLoading && !loadError" :event="event" style="padding-top: 20px; padding-bottom: 20px;" />
      <EventProtectionView v-show="value === 'Protecciones'" v-if="!showLoading && !loadError" :event="event" :slug="event.slug || ''" @event-updated="applyEventUpdate" />
      <AssistanceList v-show="value === 'Asistencia'" v-if="!showLoading && !loadError" :event="event" style="padding-top: 20px; padding-bottom: 20px;" />
      <EventFinance v-show="value === 'Finanzas'" v-if="!showLoading && !loadError" :event="event" style="padding-top: 20px; padding-bottom: 20px;" />

      <p v-if="loadError" class="workspace-feedback workspace-feedback--error">{{ loadError }}</p>
    </div>
  </div>
</template>

<script>
import { apiService } from '../../common/api.services.js'
import { formatCurrencyAmount, normalizeCurrencyCode } from '../../common/currency.js'
import TestView from './TestView.vue'
import PromoTable from './PromoTable.vue'
import AssistanceList from './AssistanceList.vue'
import EventFinance from './EventFinance.vue'
import EventEditPanel from './EventEditPanel.vue'
import EventProtectionView from './EventProtectionView.vue'
import StagesView from './StagesView.vue'
import CodesView from './CodesView.vue'
import HeaderAdmin from '../../components/HeaderAdmin.vue'

export default {
  name: 'EventHostView',

  components: {
    PromoTable,
    TestView,
    EventFinance,
    EventEditPanel,
    EventProtectionView,
    HeaderAdmin,
    AssistanceList,
    StagesView,
    CodesView,
  },

  props: {
    slug: {
      required: true,
    },
  },

  data() {
    return {
      value: 'Resumen',
      event: {},
      next: null,
      launching: false,
      eventTickets: [],
      showLoading: false,
      loadError: '',
      total_tickets: [],
      generatedData: {},
      generatedData2: {},
      tabs: [
        { name: 'Resumen', icon: 'fas fa-chart-pie' },
        { name: 'Editar', icon: 'fas fa-pen-ruler' },
        { name: 'Etapas', icon: 'fas fa-layer-group' },
        { name: 'Codigos', icon: 'fas fa-key' },
        { name: 'Promociones', icon: 'fas fa-bullhorn' },
        { name: 'Protecciones', icon: 'fas fa-shield-alt' },
        { name: 'Asistencia', icon: 'fas fa-user-check' },
        { name: 'Finanzas', icon: 'fas fa-wallet' },
      ],
    }
  },

  computed: {
    eventId() {
      return this.slug
    },

    canViewInternalFinance() {
      return Boolean(this.event && this.event.can_view_internal_finance)
    },

    eventVenueName() {
      if (this.event && this.event.venue_detail && this.event.venue_detail.name) {
        return this.event.venue_detail.name
      }

      if (this.event && this.event.venue && this.event.venue.name) {
        return this.event.venue.name
      }

      return 'Venue pendiente'
    },

    eventStatusLabel() {
      if (!this.event || !this.event.id) {
        return 'Cargando evento'
      }

      if (this.event.review_status === 'pending') {
        return 'Revisión pendiente'
      }

      if (this.event.review_status === 'changes_requested') {
        return 'Cambios solicitados'
      }

      if (this.event.review_status === 'rejected') {
        return 'Revisión rechazada'
      }

      if (Number(this.event.type) !== 1) {
        return 'Aprobado, no lanzado'
      }

      if (Number(this.event.access) === 1) {
        return Number(this.event.total_ticket_pending || 0) > 0 ? 'Venta activa con pendientes' : 'Venta activa'
      }

      return 'Acceso con código'
    },

    editModeLabel() {
      return this.event && this.event.event_edit_policy && this.event.event_edit_policy.approval_mode === 'manual'
        ? 'Edición con revisión manual'
        : 'Edicion directa activa'
    },

    heroHeadline() {
      if (!this.event || !this.event.id) {
        return 'Cargando panel del evento'
      }

      if (this.event.review_status === 'pending') {
        return 'Esperando aprobación del equipo FOMO'
      }

      if (this.event.review_status === 'changes_requested') {
        return 'Haz ajustes y vuelve a dejarlo listo'
      }

      if (this.event.review_status === 'rejected') {
        return 'Necesita ajustes antes de publicarse'
      }

      if (Number(this.event.type) !== 1) {
        return 'Aprobado y listo para lanzar'
      }

      const pending = Number(this.event.total_ticket_pending || 0)
      if (pending > 0) {
        return `${pending} tickets esperan validacion`
      }

       const sales = Number(this.event.total_ticket_sales || 0)
       return sales > 0 ? `${this.formatMoney(sales)} en venta aprobada` : 'Listo para abrir ventas'
    },

    heroSubline() {
      if (!this.event || !this.event.id) {
        return 'Estamos preparando el resumen, la edición y la operación de este evento.'
      }

      if (this.event.review_status === 'pending') {
        return 'Tu evento ya quedó cargado. Mientras revisamos la salida, puedes ajustar etapas, códigos, promos y protecciones.'
      }

      if (this.event.review_status === 'changes_requested') {
        return 'Haz los ajustes que te pedimos y vuelve a dejarlo listo para una nueva revisión.'
      }

      if (this.event.review_status === 'rejected') {
        return 'Haz los cambios necesarios y vuelve a dejarlo listo para revisión desde este mismo panel.'
      }

      if (Number(this.event.type) !== 1) {
        return 'El equipo ya lo aprobó. Ahora tú decides cuándo lanzarlo al público.'
      }

      if (Number(this.event.total_ticket_count || 0) > 0) {
        return `${this.event.total_ticket_count} accesos vendidos, ${this.event.total_is_courtesy_count || 0} cortesías y ${this.event.total_codes_active || 0} códigos activos.`
      }

       return 'Ajusta etapas, códigos y protecciones antes de abrir o relanzar la venta.'
    },

    spotlightMetricLabel() {
      if (this.event.review_status === 'pending') {
        return 'Estado'
      }

      if (this.event.review_status === 'changes_requested') {
        return 'Estado'
      }

      if (this.event.review_status === 'rejected') {
        return 'Estado'
      }

      if (Number(this.event.type) !== 1) {
        return 'Lanzamiento'
      }

      if (Number(this.event.total_ticket_pending || 0) > 0) {
        return 'Pendientes por validar'
      }

       return 'Venta aprobada'
    },

    spotlightMetricValue() {
      if (this.event.review_status === 'pending') {
        return 'Pendiente de aprobación'
      }

      if (this.event.review_status === 'changes_requested') {
        return 'Ajustes solicitados'
      }

      if (this.event.review_status === 'rejected') {
        return 'Ajustes pendientes'
      }

      if (Number(this.event.type) !== 1) {
        return 'Listo para lanzar'
      }

      if (Number(this.event.total_ticket_pending || 0) > 0) {
        return `${this.event.total_ticket_pending} tickets`
      }

       return this.formatMoney(this.event.total_ticket_sales)
    },

    canLaunchEvent() {
      return Boolean(this.event && this.event.id && this.event.review_status === 'approved' && Number(this.event.type) !== 1)
    },

    launchButtonLabel() {
      return Number(this.event && this.event.type) === 1 ? 'Evento lanzado' : 'Lanzar evento'
    },
  },

  created() {
    this.syncTabFromRoute()
    this.getEvent()
  },

  methods: {
    tabNameByQuery(value) {
      const queryValue = typeof value === 'string' ? value.toLowerCase() : ''
      const map = {
        resumen: 'Resumen',
        editar: 'Editar',
        etapas: 'Etapas',
        codigos: 'Codigos',
        'códigos': 'Codigos',
        promociones: 'Promociones',
        protecciones: 'Protecciones',
        asistencia: 'Asistencia',
        finanzas: 'Finanzas',
      }

      return map[queryValue] || 'Resumen'
    },

    tabQueryByName(value) {
      const map = {
        Resumen: 'resumen',
        Editar: 'editar',
        Etapas: 'etapas',
        Codigos: 'codigos',
        Promociones: 'promociones',
        Protecciones: 'protecciones',
        Asistencia: 'asistencia',
        Finanzas: 'finanzas',
      }

      return map[value] || 'resumen'
    },

    syncTabFromRoute() {
      this.value = this.tabNameByQuery(this.$route.query.tab)
    },

    syncRouteFromTab() {
      const current = typeof this.$route.query.tab === 'string' ? this.$route.query.tab.toLowerCase() : ''
      const next = this.tabQueryByName(this.value)

      if ((current === '' && next === 'resumen') || current === next) {
        return
      }

      const query = { ...this.$route.query }

      if (next === 'resumen') {
        delete query.tab
      } else {
        query.tab = next
      }

      this.$router.replace({
        name: 'EventHostView',
        params: { slug: this.slug },
        query,
      })
    },

    formatMoney(value) {
      return formatCurrencyAmount(value, normalizeCurrencyCode(this.event && this.event.presentment_currency, 'USD'))
    },

    formatDateLabel(value) {
      if (!value) {
        return 'Fecha por definir'
      }

      return new Intl.DateTimeFormat('es-EC', { dateStyle: 'medium' }).format(new Date(value))
    },

    openPublicEvent() {
      if (!this.event.slug || this.event.review_status !== 'approved' || Number(this.event.type) !== 1) {
        return
      }

      window.open(`${window.location.origin}/event/${this.event.slug}`, '_blank')
    },

    launchEvent() {
      if (!this.canLaunchEvent || this.launching) {
        return
      }

      this.launching = true
      apiService(`/api/host/events/${this.eventId}/launch/`, 'POST', {}).then((data) => {
        if (!data || data.err) {
          this.loadError = (data && data.err) || 'No pudimos lanzar el evento.'
          return
        }

        this.event = this.normalizeEventPayload(data)
      }).finally(() => {
        this.launching = false
      })
    },

    getEvent() {
      this.showLoading = true
      this.loadError = ''
      apiService(`/api/host/events/${this.eventId}/`).then((data) => {
        if (!data || data.err) {
          this.loadError = (data && data.err) || 'No pudimos cargar este evento.'
          this.showLoading = false
          return
        }

        this.event = this.normalizeEventPayload(data)
        this.eventTickets = []
        this.next = null
        this.getEventTickets()
      })
    },

    getEventTickets() {
      const eventLookup = this.event && this.event.slug ? this.event.slug : this.eventId
      let endpoint = `/api/event-tickets-report/?event=${encodeURIComponent(eventLookup)}`

      if (this.next) {
        endpoint = this.next
      }

      apiService(endpoint).then((data) => {
        if (data && data.err) {
          this.loadError = data.err
          this.showLoading = false
          return
        }

        const items = Array.isArray(data) ? data : (data && data.results ? data.results : [])
        this.eventTickets = this.eventTickets.concat(items)
        this.next = data && data.next ? data.next : null
        this.showLoading = false
        this.populateData()
        this.populateTrafficData()
      })
    },

    populateData() {
      const labelArr = []
      const backgroundColorArr = []
      const dataArr = []
      const palette = ['#7ad7cc', '#8bbce2', '#dce8f2', '#5fa9bb', '#76d6c8']

      this.total_tickets = []

      for (let index = 0; index < this.eventTickets.length; index += 1) {
        const ticket = this.eventTickets[index]
        const label = `${ticket.name}-${ticket.code_display || ticket.code || 'PUBLIC'}`
        labelArr.push(label)
        backgroundColorArr.push(palette[index % palette.length])

        const sold = ticket.initial_quantity - ticket.host_remaining_tickets
        dataArr.push(sold)
        this.total_tickets.push({
          id: ticket.id,
          sect: label,
          tkt: sold,
          rem: ticket.remaining_tickets,
        })
      }

      this.generatedData = {
        labels: labelArr,
        datasets: [
          {
            label: 'Tickets x seccion',
            backgroundColor: backgroundColorArr,
            data: dataArr,
          },
        ],
      }
    },

    populateTrafficData() {
      const traffic = this.event.traffic_sources || {}

      this.generatedData2 = {
        labels: ['FB/IG', 'Tiktok', 'Correo', 'Promotores', 'Sitio web'],
        datasets: [
          {
            label: 'Fuentes de trafico',
            backgroundColor: ['#7ad7cc', '#5fa9bb', '#dce8f2', '#8bbce2', '#76d6c8'],
            data: [
              traffic.Meta || 0,
              traffic.TIKTOK || 0,
              traffic.Mail || 0,
              traffic.Ambassador || 0,
              traffic.Page || 0,
            ],
          },
        ],
      }
    },

    normalizeEventPayload(payload) {
      const source = payload || {}
      return {
        ...this.event,
        ...source,
        city_id: source.city_id || (source.city && source.city.id) || source.city || this.event.city_id || '',
        category_id: source.category_id || (source.category && source.category.id) || source.category || this.event.category_id || '',
        venue_id: source.venue_id || (source.venue && source.venue.id) || source.venue || this.event.venue_id || '',
        city_detail: source.city_detail || this.event.city_detail || null,
        category_detail: source.category_detail || this.event.category_detail || null,
        venue_detail: source.venue_detail || this.event.venue_detail || null,
        event_edit_policy: source.event_edit_policy || this.event.event_edit_policy || { approval_mode: 'auto' },
        event_edit_result: source.event_edit_result || this.event.event_edit_result || { approval_mode: 'auto', applied_fields: [] },
      }
    },

    applyEventUpdate(updatedEvent) {
      this.event = this.normalizeEventPayload(updatedEvent)
    },
  },

  watch: {
    '$route.query.tab'() {
      this.syncTabFromRoute()
    },

    value() {
      this.syncRouteFromTab()
    },
  },
}
</script>

<style scoped>
.workspace-hero--event {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.85fr);
  gap: 18px;
  align-items: stretch;
  padding: 24px;
}

.workspace-hero__content--event {
  display: grid;
  gap: 14px;
}

.workspace-hero__context {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.workspace-context-pills {
  justify-content: flex-end;
}

.workspace-hero__notice {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  align-items: start;
  padding: 16px 18px;
  border-radius: 22px;
  background: rgba(140, 243, 232, 0.08);
  border: 1px solid rgba(140, 243, 232, 0.12);
}

.workspace-hero__notice i {
  color: #8cf3e8;
  font-size: 1rem;
  margin-top: 2px;
}

.workspace-hero__notice strong {
  color: #eef8ff;
}

.workspace-hero__notice small {
  display: block;
  margin-top: 4px;
  color: rgba(221, 232, 244, 0.76);
  line-height: 1.55;
}

.workspace-hero__summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.workspace-hero__summary article {
  display: grid;
  gap: 4px;
  padding: 14px 16px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(194, 212, 224, 0.08);
}

.workspace-hero__summary small {
  color: rgba(208, 219, 238, 0.74);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.workspace-hero__summary strong,
.workspace-hero__summary span {
  color: #eef4ff;
  line-height: 1.35;
}

.workspace-event-spotlight {
  position: relative;
  overflow: hidden;
  min-height: 280px;
  border-radius: 28px;
  border: 1px solid rgba(194, 212, 224, 0.1);
  background:
    radial-gradient(circle at 16% 0%, rgba(221, 228, 239, 0.08), transparent 26%),
    linear-gradient(180deg, rgba(10, 12, 18, 0.72), rgba(8, 9, 13, 0.96));
  box-shadow: 0 24px 50px rgba(0, 0, 0, 0.24);
}

.workspace-event-spotlight__media,
.workspace-event-spotlight__placeholder {
  width: 100%;
  height: 100%;
}

.workspace-event-spotlight__media {
  display: block;
  object-fit: cover;
}

.workspace-event-spotlight__placeholder {
  display: grid;
  align-content: end;
  gap: 8px;
  padding: 20px;
}

.workspace-event-spotlight__placeholder span,
.workspace-event-spotlight__footer small {
  color: rgba(221, 228, 241, 0.78);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.workspace-event-spotlight__placeholder strong,
.workspace-event-spotlight__footer strong {
  color: #f5f8ff;
  font-family: 'Syne', sans-serif;
  font-size: clamp(1.8rem, 2.4vw, 2.7rem);
  line-height: 0.94;
}

.workspace-event-spotlight__overlay {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(7, 8, 12, 0.08) 0%, rgba(7, 8, 12, 0.24) 42%, rgba(7, 8, 12, 0.9) 100%),
    radial-gradient(circle at 12% 0%, rgba(255, 255, 255, 0.14), transparent 24%);
}

.workspace-event-spotlight__copy {
  position: absolute;
  inset: auto 18px 18px 18px;
  display: grid;
  gap: 12px;
}

.workspace-event-spotlight__metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.workspace-event-spotlight__metrics span {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(194, 212, 224, 0.1);
  color: #ecf2ff;
  font-size: 0.78rem;
}

.workspace-event-spotlight__footer {
  display: grid;
  gap: 4px;
}

.workspace-metric p {
  margin: 8px 0 0;
  color: rgba(208, 219, 238, 0.74);
  line-height: 1.45;
}

.workspace-button--ghost {
  background: rgba(11, 19, 24, 0.9) !important;
  border-color: rgba(194, 212, 224, 0.12) !important;
}

.workspace-toolbar--tabs {
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;
  width: 100%;
  overflow-x: auto;
  padding: 2px 2px 6px;
  scrollbar-width: none;
}

.workspace-toolbar--tabs::-webkit-scrollbar {
  display: none;
}

.workspace-tab {
  position: relative;
  overflow: hidden;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 10px;
  min-height: 50px;
  padding: 0 18px;
  border: 1px solid rgba(194, 212, 224, 0.1);
  border-radius: 999px;
  background:
    radial-gradient(circle at 18% 0%, rgba(255, 255, 255, 0.14), transparent 34%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.03) 20%, rgba(8, 14, 20, 0.46) 100%);
  color: var(--text-soft);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.12),
    inset 0 -18px 26px rgba(23, 56, 63, 0.18);
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  white-space: nowrap;
}

.workspace-tab.is-active {
  color: #091114;
  background: linear-gradient(135deg, #dff4ff, #c2ffe9, #8cf3e8);
  border-color: transparent;
}

@media (max-width: 720px) {
  .workspace-hero--event {
    grid-template-columns: 1fr;
  }

  .workspace-hero__context,
  .workspace-context-pills {
    display: grid;
  }

  .workspace-hero__summary {
    grid-template-columns: 1fr;
  }

  .workspace-event-spotlight {
    min-height: 220px;
  }

  .workspace-event-spotlight__footer strong {
    font-size: 1.8rem;
  }
}
</style>
